import { FC, PropsWithChildren, useLayoutEffect, useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import Navbar from '@/layouts/Default/Navbar.tsx';
import { Button, Drawer } from 'react-daisyui';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { DefaultLayoutProvider, useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { cn } from '@/utils/classNames.ts';
import SmallLogo from '@/assets/small-logo.svg';
import { trackPageView } from '@/utils/analytics';

type LayoutProps = {
  mainClassName?: string;
};
const Layout: FC<LayoutProps> = (props) => {
  return (
    <DefaultLayoutProvider>
      <LayoutInner {...props} />
    </DefaultLayoutProvider>
  );
};

const LayoutInner: FC<LayoutProps> = ({ mainClassName }) => {
  const { t } = useTranslation();
  const { containerElementRef, isMenuOpen, setIsMenuOpen } = useDefaultLayoutContext();
  const location = useLocation();
  const isAdmin = location.pathname.includes('/admin');

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <>
      <div className="flex grow min-h-screen flex-col lg:flex-row">
        <aside className={cn(
          'flex lg:flex-col gap-6 px-4 py-6 lg:px-10 lg:py-10 lg:w-[136px] lg:min-w-[136px] shadow-sm max-h-[100dvh] sticky top-0 z-10',
          isAdmin ? 'bg-[#0f172a]' : 'bg-white'
        )}>
          <div className="flex items-center justify-center">
            <Link to={Routes.DASHBOARD}>
              <img src={SmallLogo} alt="Past Paper Plus" className={cn('w-[32px] h-[49px]', isAdmin && 'brightness-0 invert')} />
            </Link>
          </div>
          <div className="flex grow flex-col justify-center items-end lg:justify-start lg:items-center">
            <Navbar className="hidden lg:flex" />
            <Button shape="circle" onClick={() => setIsMenuOpen((prev) => !prev)} className={cn('p-3 lg:hidden', isAdmin && 'text-white hover:bg-white/10')}>
              <Bars3Icon />
            </Button>
          </div>
        </aside>
        <main
          ref={containerElementRef}
          id="default-layout-content-container"
          className={cn(
            'z-0 bg-base-200 relative flex flex-col grow gap-4 lg:max-h-screen lg:overflow-auto p-4 md:p-10 md:pt-3 lg:p-12 lg:pt-3 lg:max-w-[calc(100%)] lg:pb-2',
            mainClassName
          )}
        >
          <Outlet />
        </main>
      </div>
      <Drawer
        className="z-10"
        end={true}
        side={
          <div className={cn('flex flex-col max-w-[100vw] max-h-[100dvh] h-[100dvh] overflow-auto', isAdmin ? 'bg-[#0f172a]' : 'bg-base-100')}>
            <div className={cn('pt-6 pb-4 px-6 flex justify-end sticky top-0 z-10', isAdmin ? 'bg-[#0f172a]' : 'bg-base-100')}>
              <Button
                color="ghost"
                size="sm"
                endIcon={<XMarkIcon className="w-6 h-6" />}
                onClick={() => setIsMenuOpen(false)}
                className={cn(isAdmin && 'text-white/70 hover:text-white hover:bg-white/10')}
              >
                {t('button.close')}
              </Button>
            </div>
            <div className="flex flex-col items-center pb-4 px-6">
              <Link to={Routes.DASHBOARD} onClick={() => setIsMenuOpen(false)}>
                <img src={SmallLogo} alt="Past Paper Plus" className={cn('w-[32px] h-[49px]', isAdmin && 'brightness-0 invert')} />
              </Link>
            </div>
            <div className="flex grow pb-6 px-6">
              <Navbar onClick={() => setIsMenuOpen(false)} className="w-[250px] max-w-full" showTitles={true} />
            </div>
          </div>
        }
        open={isMenuOpen}
        onClickOverlay={() => (isMenuOpen ? setIsMenuOpen(false) : null)}
      ></Drawer>
      <LayoutDrawer />
    </>
  );
};

const InnerDrawer: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={'layout-drawer'}>
      {children}
    </Portal>
  );
};

const LayoutDrawer: FC = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useDefaultLayoutContext();
  const { t } = useTranslation();

  return (
    <Drawer
      className="z-10"
      end={true}
      side={
        <div className="flex flex-col max-w-[100vw] max-h-[100dvh] h-[100dvh] bg-base-100 overflow-auto">
          <div className="bg-base-100 pt-6 pb-4 px-6 flex justify-end sticky top-0 z-10">
            <Button
              color="ghost"
              size="sm"
              endIcon={<XMarkIcon className="w-6 h-6" />}
              onClick={() => setIsDrawerOpen(false)}
            >
              {t('button.close')}
            </Button>
          </div>
          <div id="layout-drawer" className="empty:hidden pb-6 px-6" />
        </div>
      }
      open={isDrawerOpen}
      onClickOverlay={() => (isDrawerOpen ? setIsDrawerOpen(false) : null)}
    ></Drawer>
  );
};

export default {
  Layout,
  Drawer: InnerDrawer,
};
